import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, profileAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const loadUser = useCallback(async () => {
        // Intentar siempre: getMe acepta JWT (header) o cookie (Google Auth)
        try {
            const response = await authAPI.getMe();
            setUser(response.data);
            setIsAuthenticated(true);

            // Load full profile
            try {
                const profileResponse = await profileAPI.get();
                setProfile(profileResponse.data);
            } catch (_) {
                // perfil opcional en carga inicial
            }
        } catch (error) {
            // 401 esperado si no hay sesión: NO mostrar error
            if (error?.response?.status !== 401) {
                console.error('Error loading user:', error);
            }
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setProfile(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const login = async (email, password) => {
        const response = await authAPI.login({ email, password });
        const { access_token, user: userData } = response.data;

        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);

        // Load full profile
        const profileResponse = await profileAPI.get();
        setProfile(profileResponse.data);

        return userData;
    };

    const register = async (nombre, email, password) => {
        const response = await authAPI.register({ nombre, email, password });
        const { access_token, user: userData } = response.data;

        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);

        return userData;
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (_) {
            // ignorar: si no había cookie, no hay nada que limpiar en backend
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setProfile(null);
        setIsAuthenticated(false);
    };

    const updateProfile = async (data) => {
        const response = await profileAPI.update(data);
        setProfile(response.data);
        setUser((prev) => ({ ...prev, ...response.data }));
        return response.data;
    };

    const refreshProfile = async () => {
        const response = await profileAPI.get();
        setProfile(response.data);
        return response.data;
    };

    const value = {
        user,
        profile,
        loading,
        isAuthenticated,
        login,
        register,
        logout,
        updateProfile,
        refreshProfile,
        reloadUser: loadUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
